import { DateTime } from 'luxon'

import {
  BUSINESS_TIME_ZONE,
  businessDayWindow as sharedBusinessDayWindow,
  businessToday,
} from '@/utils'
import type { BusinessDayUtcWindow } from '@/utils'
import type { MonthlySalesSlot } from '../types/dashboard.types'

export { businessToday }

export interface UtcWindow {
  /** ISO UTC instant, inclusive start. */
  from: string
  /** ISO UTC instant, exclusive end. */
  to: string
}

export interface BusinessDayRevenue {
  /** Business-local calendar date `YYYY-MM-DD`. */
  date: string
  revenue: number
  /** True when the business day is fully elapsed. */
  isComplete: boolean
}

/** Fixed UTC offset hours for the business zone, derived from the IANA zone. */
export const BUSINESS_UTC_OFFSET_HOURS =
  -DateTime.fromObject({ year: 2025, month: 1, day: 1 }, { zone: BUSINESS_TIME_ZONE }).offset / 60

export function nowInBusinessZone(): DateTime {
  return DateTime.now().setZone(BUSINESS_TIME_ZONE)
}

/** 24h UTC window covering one business-local day `[D 00:00, D+1 00:00) La Paz`. */
export function businessDayWindow(day: DateTime): UtcWindow {
  const date = day.setZone(BUSINESS_TIME_ZONE).toISODate() ?? businessToday()
  const window: BusinessDayUtcWindow = sharedBusinessDayWindow(date)
  return window
}

/** UTC window covering the business-local calendar month `[month 1st 00:00, next month 1st 00:00) La Paz`. */
export function businessMonthWindow(year: number, month: number): UtcWindow {
  const start = DateTime.fromObject({ year, month, day: 1 }, { zone: BUSINESS_TIME_ZONE })
  const end = start.plus({ months: 1 })
  return {
    from: toUtcIso(start),
    to: toUtcIso(end),
  }
}

function toUtcIso(value: DateTime): string {
  return value.toUTC().toISO() ?? ''
}

/** UTC hour → La Paz hour: `(utcHour - 4 + 24) % 24`. */
export function laPazHour(utcHour: number): number {
  return (utcHour - BUSINESS_UTC_OFFSET_HOURS + 24) % 24
}

/** Business-local date of a UTC slot: `D` when `utcHour >= 4`, otherwise the previous business date. */
export function utcSlotToBusinessDate(utcDate: string, utcHour: number): string {
  return (
    DateTime.fromISO(utcDate, { zone: 'utc' })
      .set({ hour: utcHour })
      .setZone(BUSINESS_TIME_ZONE)
      .toISODate() ?? ''
  )
}

/**
 * Group zero-filled monthly UTC slots into business days and sum revenue per
 * day. Each business day is 24 consecutive slots starting at `D 04:00Z`.
 */
export function groupMonthlySlotsIntoBusinessDays(
  slots: MonthlySalesSlot[],
  today: DateTime = nowInBusinessZone(),
): BusinessDayRevenue[] {
  const byDate = new Map<string, number>()

  for (const slot of slots) {
    const date = utcSlotToBusinessDate(slot.utcDate, slot.utcHour)
    byDate.set(date, (byDate.get(date) ?? 0) + slot.revenue)
  }

  const todayDate = today.toISODate() ?? ''
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, revenue]) => ({
      date,
      revenue,
      isComplete: date < todayDate,
    }))
}
